import { getApiUrl, saveReturnPath } from './api';
import { rememberPendingLink } from './auth-link-return';
import { request } from '@reflex/ui/client/http';

const POPUP_WIDTH = 500;
const POPUP_HEIGHT = 650;

interface ConnectResult {
  provider: string;
  token?: string;
  error?: string;
  /**
   * Populated by the server when the callback ran in link mode (i.e.
   * the user was already signed in and just attached a new identity).
   * Distinct from `token` because link mode does NOT issue a new
   * session — the caller refreshes the identity list rather than
   * swapping the session cookie.
   */
  context?: { linked?: '1' };
}

/**
 * Opens the OAuth provider connection flow in a popup window.
 *
 * Resolves on the first of two signals:
 *
 * 1. The popup's `postMessage` (`{ provider, token | error }`) — canonical
 *    happy/error path. Drives the success/failure toast in the consumer.
 *    Blocked by `Cross-Origin-Opener-Policy: same-origin` on some provider
 *    auth screens (notably Google), in which case it never arrives.
 * 2. A `focus` event on the parent window — the universal "popup is gone"
 *    signal that survives COOP. When the popup closes (via `window.close()`
 *    in the OAuth callback HTML) the parent regains focus and we resolve
 *    with `undefined`. Consumers refetch provider state from the server in
 *    a `finally` block, so the row updates regardless of whether
 *    `postMessage` ever fires.
 *
 * Trade-off: the parent window can also gain focus while the popup is still
 * open (the user clicks back to the parent). We accept the resulting
 * "early" refetch — invalidation is idempotent and TanStack Query's
 * `refetchOnWindowFocus: true` would refetch in that scenario anyway.
 *
 * Falls back to full-page navigation when the popup is blocked.
 */
/**
 * Connect mode for the popup OAuth flow.
 *
 *  - `'popup'` (legacy) → the server treats this as a sign-in popup
 *    that issues a fresh session token on success. Used when the
 *    user is not yet signed in (e.g. a re-auth prompt).
 *  - `'link'` → the client first calls an authenticated link-start
 *    endpoint, then navigates the popup to the returned provider URL.
 *    The server signs the user id into OAuth state and ensures the
 *    callback can verify the normal session cookie before attaching
 *    the new identity. This is the canonical "Connect another account"
 *    intent and is the default for Settings.
 */
type ConnectMode = 'popup' | 'link';

export function connectProviderPopup(
  provider: string,
  mode: ConnectMode = 'link',
  /**
   * API path of a POST endpoint that mints the link and answers `{ url }`,
   * for callers that need more than the deployment-wide
   * `/auth/:provider/link-start` (e.g. the org-targeted SSO link,
   * `/auth/oidc/realm-link-start?org=<slug>`, which needs the caller's org
   * slug). Link starts are always POSTs so a cross-site page cannot trigger
   * them. Only consulted when `mode === 'link'`.
   */
  linkStartPath?: string,
): Promise<ConnectResult | undefined> {
  const effectiveLinkStartPath = linkStartPath ?? `/auth/${provider}/link-start`;
  // The popup HTML is served from the API origin (same-origin when `BASE`
  // is `/api`, otherwise the absolute API host). Resolve it once so the
  // `message` listener can drop events from any other origin — without
  // this check, a malicious window could spoof a `{ provider, token }`
  // payload and trick us into resolving with their token.
  const expectedOrigin = new URL(getApiUrl(`/auth/${provider}/callback`), window.location.origin)
    .origin;
  const startUrl = mode === 'link' ? 'about:blank' : getApiUrl(`/auth/${provider}?mode=${mode}`);

  const left = window.screenX + (window.outerWidth - POPUP_WIDTH) / 2;
  const top = window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2;
  const features = `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},popup=yes`;

  const popup = window.open(startUrl, `oauth_${provider}`, features);

  if (!popup || popup.closed) {
    if (mode === 'link') return startLinkInPage(provider, effectiveLinkStartPath);
    saveReturnPath();
    window.location.assign(getApiUrl(`/auth/${provider}`));
    return new Promise(() => {});
  }

  return new Promise<ConnectResult | undefined>((resolve) => {
    let settled = false;

    function settle(result: ConnectResult | undefined) {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      window.removeEventListener('focus', onFocus);
      resolve(result);
    }

    function onMessage(event: MessageEvent) {
      if (event.origin !== expectedOrigin) return;
      if (!event.data || typeof event.data !== 'object') return;
      if (!('provider' in event.data)) return;
      const data = event.data as ConnectResult;
      if (data.provider !== provider) return;
      settle(data);
    }

    function onFocus() {
      settle(undefined);
    }

    window.addEventListener('message', onMessage);
    window.addEventListener('focus', onFocus);

    if (mode === 'link') {
      void request<{ url: string }>(effectiveLinkStartPath, { method: 'POST' })
        .then(({ url }) => {
          if (!popup.closed) {
            popup.location.href = url;
          }
        })
        .catch((err: unknown) => {
          popup.close();
          settle({ provider, error: err instanceof Error ? err.message : 'link_start_failed' });
        });
    }
  });
}

/**
 * Run an account link in the current tab: the popup-blocked fallback, and the
 * retry after a full-page link failed. Runs the same authenticated POST as the
 * popup and navigates the page to the provider. The link callback page brings
 * the tab back to the app, where `captureAuthLinkOutcome` restores the page
 * the user started from.
 *
 * The return path is saved only once the page is actually leaving, so a
 * refused start leaves no stale return path behind; it resolves with that
 * error instead.
 */
export function startLinkInPage(
  provider: string,
  linkStartPath: string,
): Promise<ConnectResult | undefined> {
  return request<{ url: string }>(linkStartPath, { method: 'POST' }).then(
    ({ url }) => {
      rememberPendingLink(provider, linkStartPath);
      window.location.assign(url);
      return new Promise<never>(() => {});
    },
    (err: unknown) => ({
      provider,
      error: err instanceof Error ? err.message : 'link_start_failed',
    }),
  );
}
